
import React from "react";
import Header from "@/components/Header";
import { ScrollToTopButton } from "@/components/ui/scroll-to-top";
import { motion } from "framer-motion";
import { PageSEO } from "@/components/seo/PageSEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <PageSEO 
        title="Frequently Asked Questions | Retail Ranker"
        description="Get answers to common questions about Retail Ranker - the software that helps brick & mortar shops improve their Google rankings."
        keywords="retail store FAQ, shop FAQ, retail software questions, Google ranking help"
      />
      
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-20 pb-12 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 z-0"></div>
          <div className="container px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Frequently Asked Questions</h1>
              <p className="text-lg text-muted-foreground">
                Got questions? We've got straight-up answers!
              </p>
            </motion.div>
          </div>
        </section>
        
        {/* Main Content */}
        <section className="py-12 md:py-16">
          <div className="container px-4 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-card rounded-xl shadow-sm p-6 md:p-8"
            >
              <Accordion type="single" collapsible className="w-full space-y-4">
                <AccordionItem value="item-1" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">What exactly is Retail Ranker?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    It's your secret weapon for boosting your store's online visibility. We help you track how customers find your shop online and improve your rankings so more people walk through your doors!
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-2" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Do I need to be tech-savvy to use this?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    Not at all! If you can use social media, you can use Retail Ranker. We've made everything super intuitive with a clean, simple interface designed for busy shop owners.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-3" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">How long does it take to set up?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    Just 5 minutes! Connect your Google Business Profile, and you're ready to roll. No complicated installation or tech headaches.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-4" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">How will this actually help my store?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    You'll discover exactly how customers are finding you online, which keywords bring in the most traffic, and how you stack up against competitors. Then you'll get actionable tips to climb those rankings!
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-5" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Can I see how I compare to other stores in my area?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    Absolutely! Our competitor analysis shows you exactly where you stand compared to similar businesses, without the guesswork.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-6" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Will this help with my Google reviews?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    You bet! You can monitor, respond to, and analyze all your Google reviews in one place, plus get insights into what customers love (or don't) about your shop.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-7" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">How much does Retail Ranker cost?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    We've designed our pricing specifically for independent retailers - not big chains with massive budgets. Our plans are affordable and tailored to small business needs.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-8" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Is there a free trial available?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    We don't currently offer a free trial, but we do have a sample dashboard you can explore to get a feel for the platform before subscribing.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-9" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">What if I want to cancel?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    No problemo! Cancel anytime with zero hassle - we don't do long-term contracts or sneaky fine print.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-10" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Does this work for multiple store locations?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    Sure does! You can manage all your locations from one dashboard, with location-specific insights for each.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-11" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Is my data secure?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    Totally! We use bank-level encryption and never share your data with third parties. Your business info stays yours.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-12" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Do I need to install anything on my computer?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    Nope! Retail Ranker is 100% web-based, so you can access it from any device with an internet connection.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-13" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Is Retail Ranker available as an iOS or Android app?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    Not currently! Retail Ranker is a web-based platform optimized for desktop and mobile browsers. This means you can access all features from any device without downloading an app - just visit our website!
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-14" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">What kind of support do you offer?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    We're here for you! Get help via email or browse our knowledge base. Plus, join our community of shop owners for extra tips and tricks.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-15" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">Can I get help setting things up?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    We offer clear setup guides and documentation to help you get started. If you need additional assistance, our support team is just an email away.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-16" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-lg font-medium py-4">What if I have feature suggestions?</AccordionTrigger>
                  <AccordionContent className="pb-4 px-1">
                    We love those! We're constantly improving based on feedback from real store owners like you. Just send your ideas our way!
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <p className="text-lg mb-4">Still have questions? We're happy to help!</p>
              <a 
                href="/contact" 
                className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 py-2 text-sm font-medium transition-colors"
              >
                Contact Us
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      
      
      <ScrollToTopButton />
    </div>
  );
};

export default FAQ;
